import { useRef } from 'react';
import { ACCENTS, COLUMN_OPTIONS } from '../../constants/options';
import { useApp } from '../../context/AppContext';
import { useI18n } from '../../i18n/useI18n';
import { useDismiss } from '../../hooks/useDismiss';
import { usePhone } from '../../hooks/usePhone';
import {
  IconCheck,
  IconChevronsLeft,
  IconCircle,
  IconEye,
  IconEyeOff,
  IconMoon,
  IconPlus,
  IconSquare,
  IconSun,
} from '../icons/Icons';
import { SegmentedControl } from '../ui/SegmentedControl';
import styles from './Sidebar.module.css';

function Row({ label, children, plain }) {
  return (
    <div className={plain ? `${styles.row} ${styles.rowPlain}` : styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      {children}
    </div>
  );
}

export function Sidebar() {
  const { state, actions } = useApp();
  const { t } = useI18n();
  const phone = usePhone();
  const prefs = state.preferences;
  const open = prefs.sidebarOpen;
  const panelRef = useRef(null);
  const columnLabel = (count) => (count === 1 ? t('columns.one') : t('columns.many', { n: count }));
  useDismiss(open, () => actions.setPref('sidebarOpen', false), [panelRef]);

  return (
    <aside
      ref={panelRef}
      className={open ? styles.sidebar : `${styles.sidebar} ${styles.closed}`}
      aria-label={t('prefs.title')}
      aria-hidden={!open}
      inert={open ? undefined : ''}
    >
      <div className={styles.inner}>
        <div className={styles.body}>
          <h2 className={styles.title}>{t('prefs.title')}</h2>
          <Row label={t('prefs.language')} plain>
            <SegmentedControl
              label={t('prefs.language')}
              value={prefs.language === 'ar' ? 'ar' : 'en'}
              onChange={(value) => actions.setPref('language', value)}
              options={[
                { value: 'en', text: t('lang.enShort'), label: t('lang.en') },
                { value: 'ar', text: t('lang.arShort'), label: t('lang.ar') },
              ]}
            />
          </Row>
          <div className={styles.swatches} role="radiogroup" aria-label={t('prefs.accent')}>
            {ACCENTS.map((accent) => {
              const selected = prefs.accent === accent.id;
              return (
                <button
                  key={accent.id}
                  type="button"
                  className={styles.swatch}
                  style={{ background: accent.value }}
                  aria-label={t(accent.labelKey)}
                  aria-checked={selected}
                  role="radio"
                  onClick={() => actions.setPref('accent', accent.id)}
                >
                  {selected && <IconCheck />}
                </button>
              );
            })}
          </div>

          <Row label={t('prefs.columns')}>
            <SegmentedControl
              label={t('prefs.columns')}
              value={prefs.columns}
              onChange={(value) => actions.setPref('columns', value)}
              options={COLUMN_OPTIONS.map((count) => ({
                value: count,
                text: String(count),
                label: columnLabel(count),
              }))}
            />
          </Row>

          <Row label={t('prefs.text')}>
            <SegmentedControl
              label={t('prefs.text')}
              value={prefs.textSize}
              onChange={(value) => actions.setPref('textSize', value)}
              options={[
                { value: 's', text: t('size.sShort'), label: t('size.s') },
                { value: 'm', text: t('size.mShort'), label: t('size.m') },
                { value: 'l', text: t('size.lShort'), label: t('size.l') },
              ]}
            />
          </Row>

          <Row label={t('prefs.spacing')}>
            <SegmentedControl
              label={t('prefs.spacing')}
              value={prefs.spacing}
              onChange={(value) => actions.setPref('spacing', value)}
              options={[
                { value: 's', text: t('size.sShort'), label: t('space.s') },
                { value: 'm', text: t('size.mShort'), label: t('space.m') },
                { value: 'l', text: t('size.lShort'), label: t('space.l') },
              ]}
            />
          </Row>

          <Row label={t('prefs.completed')}>
            <SegmentedControl
              label={t('prefs.completedLabel')}
              value={prefs.showCompleted}
              onChange={(value) => actions.setPref('showCompleted', value)}
              options={[
                { value: true, label: t('prefs.showDone'), icon: <IconEye /> },
                { value: false, label: t('prefs.hideDone'), icon: <IconEyeOff /> },
              ]}
            />
          </Row>

          <Row label={t('prefs.position')}>
            <SegmentedControl
              label={t('prefs.position')}
              value={prefs.completedPosition}
              onChange={(value) => actions.setPref('completedPosition', value)}
              options={[
                { value: 'keep', text: t('position.keep'), label: t('position.keepLabel') },
                { value: 'end', text: t('position.end'), label: t('position.endLabel') },
              ]}
            />
          </Row>

          <Row label={t('prefs.bullet')}>
            <SegmentedControl
              label={t('prefs.bullet')}
              value={prefs.bullet}
              onChange={(value) => actions.setPref('bullet', value)}
              options={[
                { value: 'circle', label: t('bullet.circle'), icon: <IconCircle /> },
                { value: 'square', label: t('bullet.square'), icon: <IconSquare /> },
                { value: 'plus', label: t('bullet.plus'), icon: <IconPlus /> },
              ]}
            />
          </Row>

          {!phone && (
            <Row label={t('prefs.start')}>
              <SegmentedControl
                label={t('prefs.start')}
                value={prefs.startOn}
                onChange={(value) => actions.setPref('startOn', value)}
                options={[
                  { value: 'today', text: t('start.today'), label: t('start.todayLabel') },
                  { value: 'yesterday', text: t('start.yesterday'), label: t('start.yesterdayLabel') },
                ]}
              />
            </Row>
          )}

          <Row label={t('prefs.lines')}>
            <SegmentedControl
              label={t('prefs.lines')}
              value={prefs.showLines}
              onChange={(value) => actions.setPref('showLines', value)}
              options={[
                { value: true, label: t('lines.show'), icon: <IconEye /> },
                { value: false, label: t('lines.hide'), icon: <IconEyeOff /> },
              ]}
            />
          </Row>

          <Row label={t('prefs.display')}>
            <SegmentedControl
              label={t('prefs.display')}
              value={prefs.theme}
              onChange={(value) => actions.setPref('theme', value)}
              options={[
                { value: 'light', text: t('theme.light'), label: t('theme.light'), icon: <IconSun /> },
                { value: 'dark', text: t('theme.dark'), label: t('theme.dark'), icon: <IconMoon /> },
              ]}
            />
          </Row>
        </div>
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.collapse}
            aria-label={t('prefs.hide')}
            data-mirror=""
            onClick={() => actions.setPref('sidebarOpen', false)}
          >
            <IconChevronsLeft />
          </button>
        </div>
      </div>
    </aside>
  );
}
